import React, { useEffect, useState } from 'react';
import './ResetPassword.css';
import { Formik, Form, Field, ErrorMessage } from 'formik';
import * as Yup from 'yup';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { useSearchParams, useNavigate } from 'react-router-dom';
import axiosInstance from '../../services/AxiosInstance';
import { Footer } from '../components';

const ResetPassword = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');
  const email = searchParams.get('email');

  const [corporateList, setCorporateList] = useState([]);
  const [selectedCorporateId, setSelectedCorporateId] = useState('');
  const [corporateDisabled, setCorporateDisabled] = useState();
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const initialValues = { password: '', confirmPassword: '' };

  const validationSchema = Yup.object({
    password: Yup.string()
      .required("Password is required")
      .min(8, "Password must be at least 8 characters")
      .matches(/[A-Z]/, "Password must contain at least one uppercase letter")
      .matches(/[a-z]/, "Password must contain at least one lowercase letter")
      .matches(/[0-9]/, "Password must contain at least one number")
      .matches(/[!@#$%^&*(),.?":{}|<>]/, "Password must contain at least one special character"),

    confirmPassword: Yup.string()
      .required("Repeat password is required")
      .oneOf([Yup.ref("password"), null], "Passwords must match"),
  });

  useEffect(() => {
    const getCorporatelist = async () => {
      try {
        const response = await axiosInstance.post('/auth/forgot-password/corporatelist', {
          email,
          token,
        });

        const list = response.data.data || [];
        setCorporateList(list);

        // Auto-select if only one corporate
        if (list.length === 1) {
          setSelectedCorporateId(list[0].corporate_id);
          setCorporateDisabled(true);
        }
      } catch (error) {
        console.error('Verification failed:', error.response?.data || error.message);
        toast.error('Invalid or expired reset link.');
      }
    };

    if (email && token) {
      getCorporatelist();
    }
  }, [email, token]);

  const onSubmit = async (values, { setSubmitting, resetForm }) => {
    try {
      await axiosInstance.post('/auth/forgotpassword/reset', {
        token,
        newPassword: values.password,
        corporateid: corporateList.length > 1 ? selectedCorporateId : corporateList[0].corporate_id,
      });

      toast.success('Password reset successful! You can now log in.');
      resetForm();
      setTimeout(() => navigate('/login'), 2000);
    } catch (error) {
      toast.error(
        error.response?.data?.message || 'Failed to reset password. Please try again.'
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="d-flex flex-column justify-content-start align-items-center min-vh-100" style={{ backgroundColor: '#f8f8f8' }}>
      <ToastContainer />
      <div className='hero-landing'>
        <h1 className="text-center mb-4 text-white display-5">Reset Password</h1>
      </div>
      <div style={{ maxWidth: 400, width: '100%', padding: "2rem", marginBottom: "5rem" }}>
        <Formik
          initialValues={initialValues}
          validationSchema={validationSchema}
          onSubmit={onSubmit}
        >
          {({ isSubmitting }) => (
            <Form>
              {/* Corporate List Dropdown */}
              {corporateList.length > 1 && (
                <div className="mb-3">
                  <label htmlFor="corporateId" className="form-label"><strong>Select Corporate</strong></label>
                  <Field
                    as="select"
                    name="corporateId"
                    className="form-control"
                    value={selectedCorporateId}
                    onChange={(e) => setSelectedCorporateId(e.target.value)}
                    disabled={corporateDisabled}
                  >
                    <option value="">-- Select Corporate --</option>
                    {corporateList.map((corp) => (
                      <option key={corp.corporate_id} value={corp.corporate_id}>
                        {corp.Corporate.name}
                      </option>
                    ))}
                  </Field>
                </div>
              )}


              <div className="mb-3">
                <label htmlFor="password" className="form-label"><strong>New Password</strong></label>
                <div className="position-relative password-input-wrapper">
                  <Field
                    type={showPassword ? "text" : "password"}
                    name="password"
                    className="form-control"
                    placeholder="Enter new password"
                  />
                  <button
                    type="button"
                    className="password-toggle-btn"
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    <i className={`fas ${showPassword ? 'fa-eye-slash' : 'fa-eye'}`}></i>
                  </button>
                </div>
                <ErrorMessage name="password" component="div" className="text-danger small mt-1" />
              </div>

              <div className="mb-3">
                <label htmlFor="confirmPassword" className="form-label"><strong>Repeat Password</strong></label>
                <div className="position-relative password-input-wrapper">
                  <Field
                    type={showConfirmPassword ? "text" : "password"}
                    name="confirmPassword"
                    className="form-control"
                    placeholder="Repeat new password"
                  />
                  <button
                    type="button"
                    className="password-toggle-btn"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  >
                    <i className={`fas ${showConfirmPassword ? 'fa-eye-slash' : 'fa-eye'}`}></i>
                  </button>
                </div>
                <ErrorMessage name="confirmPassword" component="div" className="text-danger small mt-1" />
              </div>
              <div className="password-info">Password must be at least 8 characters long and contain at least one uppercase letter, one number, and one special symbol.</div>
              <button
                type="submit"
                className="reset-password-btn"
                disabled={isSubmitting || !selectedCorporateId}
              >
                {isSubmitting ? 'Resetting...' : 'Reset Password'}
              </button>
            </Form>
          )}
        </Formik>
      </div>
      <Footer />
    </div>
  );
};

export default ResetPassword;
